import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, Search, ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  legalService,
  type AdminTermsAcceptanceResponse,
  type AdminTermsUserRow,
} from '@/services/legal.service'

type StatusFilter = 'all' | AdminTermsUserRow['terms_status']

const STATUS_LABEL: Record<AdminTermsUserRow['terms_status'], string> = {
  accepted_current: 'Accettato (versione corrente)',
  accepted_outdated: 'Versione vecchia',
  never_accepted: 'Mai accettato',
  admin_exempt: 'Admin (esente)',
}

const STATUS_BADGE_CLASS: Record<AdminTermsUserRow['terms_status'], string> = {
  accepted_current: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  accepted_outdated: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  never_accepted: 'bg-red-500/15 text-red-400 border-red-500/30',
  admin_exempt: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const truncateUA = (ua: string | null): string => {
  if (!ua) return '—'
  return ua.length > 60 ? `${ua.slice(0, 57)}...` : ua
}

export const TermsAcceptance = () => {
  const [data, setData] = useState<AdminTermsAcceptanceResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [search, setSearch] = useState('')

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await legalService.getAdminTermsAcceptance({
        status: statusFilter,
        search: search.trim() || undefined,
      })
      setData(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore di caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  // Search debounced
  useEffect(() => {
    const t = setTimeout(() => {
      fetchData()
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const stats = data?.stats
  const currentVersion = data?.current_version ?? '—'
  const users = useMemo(() => data?.users ?? [], [data])

  return (
    <div className="space-y-6">
      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-400" />
            Versione contratto attualmente richiesta
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 flex-wrap">
            <code className="text-lg font-mono px-3 py-1 rounded bg-slate-800 border border-slate-700">
              {currentVersion}
            </code>
            <span className="text-sm text-muted-foreground">
              Gli utenti non-admin che hanno accettato una versione diversa devono ri-accettare al prossimo accesso a /trading/*.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Totale utenti"
          value={stats?.total ?? 0}
          loading={loading}
          icon={<ShieldQuestion className="w-4 h-4" />}
          tone="slate"
        />
        <StatCard
          label="Accettato corrente"
          value={stats?.accepted_current ?? 0}
          loading={loading}
          icon={<ShieldCheck className="w-4 h-4" />}
          tone="emerald"
        />
        <StatCard
          label="Versione vecchia"
          value={stats?.accepted_outdated ?? 0}
          loading={loading}
          icon={<ShieldAlert className="w-4 h-4" />}
          tone="amber"
        />
        <StatCard
          label="Mai accettato"
          value={stats?.never_accepted ?? 0}
          loading={loading}
          icon={<ShieldX className="w-4 h-4" />}
          tone="red"
        />
        <StatCard
          label="Admin (esenti)"
          value={stats?.admins ?? 0}
          loading={loading}
          icon={<ShieldQuestion className="w-4 h-4" />}
          tone="slate"
        />
      </div>

      {/* Tabella utenti + filtri */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle>Stato accettazione per utente</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Email o nome..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as StatusFilter)}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="accepted_current">Accettato corrente</SelectItem>
                  <SelectItem value="accepted_outdated">Versione vecchia</SelectItem>
                  <SelectItem value="never_accepted">Mai accettato</SelectItem>
                  <SelectItem value="admin_exempt">Admin (esente)</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchData} disabled={loading} title="Ricarica">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 rounded border border-red-500/40 bg-red-500/10 text-sm text-red-400">
              {error}
            </div>
          )}

          {loading && !data ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Caricamento utenti...
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nessun utente corrisponde ai filtri.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700/60 text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Utente</th>
                    <th className="py-2 pr-4 font-medium">Stato</th>
                    <th className="py-2 pr-4 font-medium">Versione accettata</th>
                    <th className="py-2 pr-4 font-medium">Quando</th>
                    <th className="py-2 pr-4 font-medium">IP</th>
                    <th className="py-2 pr-4 font-medium">User agent</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="py-2 pr-4">
                        <div className="font-medium">{u.email}</div>
                        {u.full_name && (
                          <div className="text-xs text-muted-foreground">{u.full_name}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className={STATUS_BADGE_CLASS[u.terms_status]}>
                          {STATUS_LABEL[u.terms_status]}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {u.terms_version_accepted ?? '—'}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {formatDate(u.terms_accepted_at)}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {u.terms_accepted_ip ?? '—'}
                      </td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground" title={u.terms_accepted_user_agent ?? undefined}>
                        {truncateUA(u.terms_accepted_user_agent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

interface StatCardProps {
  label: string
  value: number
  loading: boolean
  icon: React.ReactNode
  tone: 'slate' | 'emerald' | 'amber' | 'red'
}

const TONE_CLASS: Record<StatCardProps['tone'], string> = {
  slate: 'text-slate-300',
  emerald: 'text-emerald-400',
  amber: 'text-amber-400',
  red: 'text-red-400',
}

const StatCard = ({ label, value, loading, icon, tone }: StatCardProps) => (
  <Card>
    <CardContent className="pt-4 pb-4">
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${TONE_CLASS[tone]}`}>
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-3xl font-semibold mt-1">
        {loading ? <Loader2 className="w-6 h-6 animate-spin opacity-50" /> : value}
      </div>
    </CardContent>
  </Card>
)
